// src/components/paxTypes/PaxTypesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetPaxTypes from "../../hooks/paxTypes/useGetPaxTypes";
import useDeletePaxTypes from "../../hooks/paxTypes/useDeletePaxTypes";
import useDeleteAllPaxTypes from "../../hooks/paxTypes/useDeleteAllPaxTypes";
import useRestorePaxType from "../../hooks/paxTypes/useRestorePaxType";
import useRestoreAllPaxTypes from "../../hooks/paxTypes/useRestoreAllPaxTypes";
import useForceDeletePaxType from "../../hooks/paxTypes/useForceDeletePaxType";
import useForceDeleteAllPaxTypes from "../../hooks/paxTypes/useForceDeleteAllPaxTypes";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import PaxTypesTableRow from "./Table/PaxTypesTableRow";
import PaxTypesTableColumns from "./Table/PaxTypesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const PaxTypesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("paxTypes_list"),
            create: has("paxTypes_create"),
            edit: has("paxTypes_edit"),
            delete: has("paxTypes_delete"),
            forceDelete: has("paxTypes_force_delete"),
            archiveList: has("paxTypes_archive_list"),
            restore: has("paxTypes_restore"),
            restoreAll: has("paxTypes_restore-all-table"),
            deleteAll: has("paxTypes_delete-all-table"),
            massForceDeleteAll: has("paxTypes_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/pax-types/archive");

    // CRUD Hooks
    const { deletePaxTypes, isDelete } = useDeletePaxTypes();
    const { deleteAllPaxTypes, isDeleteAll } = useDeleteAllPaxTypes();
    const { restorePaxType, isRestore } = useRestorePaxType();
    const { restoreAllPaxTypes, isRestoreAll } = useRestoreAllPaxTypes();
    const { forceDeletePaxType, isForceDelete } = useForceDeletePaxType();
    const { forceDeleteAllPaxTypes, isForceDeleteAll } = useForceDeleteAllPaxTypes();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/pax-types",
        deleteSingle: deletePaxTypes,
        deleteAll: deleteAllPaxTypes,
        restoreSingle: restorePaxType,
        restoreAll: restoreAllPaxTypes,
        forceDeleteSingle: forceDeletePaxType,
        forceDeleteAll: forceDeleteAllPaxTypes,
        isArchive,
    });

    // ✨ Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    // ✨ Filters including status and sorting
    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    // ✨ Filter configuration for TableFilterBar
    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    // ✨ Clear all filters handler
    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, paxTypes, meta } = useGetPaxTypes(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => paxTypes?.items || [], [paxTypes]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <PaxTypesTableRow
            key={params.item.id}
            paxType={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "أنواع الركاب" : "Pax Types";
    const subtitle = isArchive
        ? isArabic
            ? "عرض أنواع الركاب المحذوفة (الأرشيف)"
            : "Archived pax types (soft deleted)"
        : isArabic
            ? "إدارة أنواع الركاب المتاحة في النظام"
            : "Manage available pax types in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة نوع ركاب"
            : "Create new pax type";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllPaxTypes({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/pax-types/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/pax-types/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/pax-types"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={PaxTypesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={
                    isArchive ? "لا توجد أنواع ركاب في الأرشيف" : "لا توجد أنواع ركاب"
                }
                emptyLabelEn={isArchive ? "No archived pax types" : "No pax types"}
                loadErrorAr="حدث خطأ أثناء تحميل أنواع الركاب"
                loadErrorEn="Failed to load pax types"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default PaxTypesTable;
